const __RELATIVE_SOURCE_FILE__ /* __INJECTED__ */ = 'libs/common_web/src/components/ToolTip/ToolTip.tsx';
import { ElementType, Fragment, ReactElement, ReactNode, useMemo } from 'react';
import { Tooltip as MuiToolTip, TooltipProps as MuiToolTipProps } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { css } from '@emotion/css';
import { colors } from '../../defs/colors';

interface Props {
    title?: string | ReactNode;
    placement?: MuiToolTipProps['placement'];
    component?: ElementType;
    enterDelay?: number;
    contentMinWidth?: number;
    wrapNewlines?: boolean;
    backgroundColor?: string;
    elevation?: number; // defines the tooltip's shadow; default is 1
    children?: ReactElement<any>;
}

function useCSSClasses(backgroundColor: string | undefined, elevation: number | undefined) {
    const theme = useTheme();
    return useMemo(
        () => ({
            tooltip: css`
                font-size: ${theme.typography.body1.fontSize};
                font-weight: ${theme.typography.body1.fontWeight};
                box-shadow: ${elevation == undefined ? theme.shadows[1] : `${theme.shadows[elevation]} !important`};
                background-color: ${backgroundColor ? `${backgroundColor} !important` : colors.grey['700']};
            `,
            arrow: css`
                color: ${backgroundColor ? `${backgroundColor} !important` : undefined};
            `,
        }),
        [theme, backgroundColor, elevation]
    );
}

export const ToolTip = (props: Props) => {
    const classes = useCSSClasses(props.backgroundColor, props.elevation);

    if (!props.title) {
        return <>{props.children}</>;
    } else {
        const theTitle = (() => {
            if (typeof props.title !== 'string' || !props.wrapNewlines) {
                return props.title;
            } else {
                return (
                    <>
                        {props.title.split('\n').map((line, idx) => {
                            return (
                                <Fragment key={idx}>
                                    {idx > 0 && <br />}
                                    {line}
                                </Fragment>
                            );
                        })}
                    </>
                );
            }
        })();

        const slots: MuiToolTipProps['slots'] = props.component ? { tooltip: props.component } : undefined;
        const slotProps: MuiToolTipProps['slotProps'] = {};

        if (props.contentMinWidth) {
            slotProps.tooltip = {
                style: {
                    minWidth: props.contentMinWidth,
                },
            };
        }

        return (
            <MuiToolTip
                title={theTitle}
                placement={props.placement}
                slots={slots}
                classes={classes}
                slotProps={slotProps}
                arrow
                enterDelay={props.enterDelay ?? 500}
            >
                {props.children as any}
            </MuiToolTip>
        );
    }
};
